import React, { useEffect, useState } from "react";
import { Modal, ModalBody, CardFooter, Button } from "reactstrap";
import { useQueryClient, useMutation } from "react-query";
import { injectIntl } from "react-intl";
import { toast } from "react-toastify";
import { FormattedMessage } from "react-intl";
import ConfirmDialog from "../Modal/ConfirmDialog";
import { GetAvailableStudentsForPeriod } from "../../actions/teacherQueries";
import { ChangeStudentsAndSupervisorsInPeriod } from "../../actions/teacherMutations";
import CreateAPLPeriodSupervisor from "./CreateAPLPeriodSupervisor";

const transformStudentsWithSupervisors = (periodStudents) =>
  periodStudents.map((stud) => ({
    ...stud.student,
    companyId: stud.companyId,
    studentId: stud.student.id,
    supervisors: stud.supervisors.map((studsup) => ({
      ...studsup.supervisor,
      isMainResponsible: studsup.isMainResponsible,
    })),
  }));

const ModifySupervisorsModal = ({
  allSupervisors,
  allStudents,
  isOpen,
  toggle,
  period,
  listOfStudentIds,
  schoolId,
  level,
  objId,
  programTeachers,
  teacherInfo,
}) => {
  const [newStudentsAndSupervisors, setNewStudentsAndSupervisors] = useState(
    []
  );
  const [selectableStudents, setSelectableStudents] = useState([]);
  const [promptRemoveStudents, setPromptRemoveStudents] = useState(false);
  const [studentsForRemoval, setStudentsForRemoval] = useState([]);
  const [postButtonDisabled, setPostButtonDisabled] = useState(false);

  const mutateChangeStudentsAndSupervisorsInPeriod = useMutation(
    ChangeStudentsAndSupervisorsInPeriod
  );

  const availableStudentIds = GetAvailableStudentsForPeriod({
    candidates: {
      periodId: period.period.id,
      candidateStudentIds: listOfStudentIds,
    },
  });

  const queryClient = useQueryClient();

  useEffect(() => {
    if (
      period?.period &&
      availableStudentIds.data &&
      !availableStudentIds.isLoading &&
      !availableStudentIds.error
    ) {
      const tempNewStudentsAndSupervisors = transformStudentsWithSupervisors(
        period.period.students
      );
      setNewStudentsAndSupervisors(tempNewStudentsAndSupervisors);

      const availableStudentIdSet = new Set([
        ...availableStudentIds.data,
        ...tempNewStudentsAndSupervisors.map((s) => s.id),
      ]);
      const tempAvailableStudents = allStudents.filter((stud) =>
        availableStudentIdSet.has(stud.id)
      );

      const seenIds = new Set();
      const selectableStudentsList = tempAvailableStudents
        .filter((s) => {
          if (seenIds.has(s.student.id)) return false;
          seenIds.add(s.student.id);
          return true;
        })
        .map((s) => ({ ...s.student }));
      setSelectableStudents(selectableStudentsList);
    }
  }, [
    period,
    availableStudentIds.isLoading,
    availableStudentIds.error,
    availableStudentIds.data,
    allStudents,
  ]);

  const saveChanges = () => {
    const allStudentsIncluded = period.period.students.every((oldStud) =>
      newStudentsAndSupervisors.some(
        (currStud) => currStud.id === oldStud.studentId
      )
    );
    if (allStudentsIncluded) {
      handlePostChanges();
    } else {
      const tempStudentsForRemoval = [];
      period.period.students.forEach((oldStud) => {
        if (
          !newStudentsAndSupervisors.some(
            (currStud) => currStud.id === oldStud.studentId
          )
        ) {
          tempStudentsForRemoval.push(oldStud);
        }
      });
      setStudentsForRemoval(tempStudentsForRemoval);
      setPromptRemoveStudents(true);
    }
  };

  const handlePostChanges = () => {
    setStudentsForRemoval([]);
    setPromptRemoveStudents(false);
    setPostButtonDisabled(true);
    const studentsToPost = [];
    newStudentsAndSupervisors.forEach((stud) => {
      let studentToAdd = {
        name: stud.name,
        companyId: stud.companyId,
        studentId: stud.id,
        supervisors: stud.supervisors,
      };
      if (studentToAdd.supervisors.length === 0) {
        studentToAdd.supervisors.push({
          ...teacherInfo.data,
          IsMainResponsible: true,
        });
      }
      studentsToPost.push(studentToAdd);
    });

    mutateChangeStudentsAndSupervisorsInPeriod.mutate(
      {
        periodId: period.period.id,
        deleteMissing: true,
        students: studentsToPost,
        studentIdsToManage: listOfStudentIds,
      },
      {
        onSuccess: (data) => {
          setPostButtonDisabled(false);
          if (data && data.status > 199 && data.status < 300) {
            toast.success(<FormattedMessage id="PeriodUpdated" />);
            handleReloadSupervisors();
            toggle();
          } else {
            toast.warn(data);
          }
        },
        throwOnError: true,
        onError: (data) => {
          setPostButtonDisabled(false);
          toast.warn(data.response.data);
        },
      }
    );
  };

  const handleReloadSupervisors = () => {
    queryClient.invalidateQueries(["FetchPeriods", [level, objId]]);
  };

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} size="xl">
        <ModalBody>
          <CreateAPLPeriodSupervisor
            students={selectableStudents}
            supervisors={allSupervisors}
            aplStudentSupervisors={newStudentsAndSupervisors}
            setAplStudentSupervisors={setNewStudentsAndSupervisors}
            schoolId={schoolId}
            programTeachers={programTeachers}
            isModifying={true}
            isOpen={isOpen}
          />
        </ModalBody>
        <CardFooter>
          <div className="justify-content-end d-flex gap-2 m-3">
            <Button outline color="secondary" onClick={() => toggle()}>
              <FormattedMessage id="Cancel" />
            </Button>
            <Button
              outline
              color="primary"
              onClick={() => saveChanges()}
              disabled={postButtonDisabled}
            >
              <FormattedMessage id="Save" />
            </Button>
          </div>
        </CardFooter>
      </Modal>
      <ConfirmDialog
        isOpen={promptRemoveStudents}
        toggle={() => setPromptRemoveStudents(!promptRemoveStudents)}
        title={<FormattedMessage id="AreYouSure" />}
        message={
          <FormattedMessage
            values={{
              students: studentsForRemoval.map((a) => " " + a.student.name),
            }}
            id="WillBeRemovedFromPeriod"
          />
        }
        onProceed={handlePostChanges}
        danger={true}
      />
    </>
  );
};
export default injectIntl(ModifySupervisorsModal);
